import { v4 as uuidv4 } from "uuid";
import stringToNumber from "./stringToNumber";
import {
  formatCurrencyOrNumber,
  getQuantityInEaches,
  roundNumber
} from "./genericFunctions";
import { calculateEaches } from "./storeCreditUtils";

export const currencyToNumber = (qty: string) => +qty?.slice(1) || 0;

export const convertToCurrency = (currency: string, amount: number) =>
  amount < 0
    ? "-" +
      currency +
      Math.abs(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    : currency +
      amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

export default convertToCurrency;

export const calculateOverShortSum = (overShortItem: any) => {
  const result =
    ((+overShortItem?.over || 0) - (+overShortItem?.short || 0)) *
    (+overShortItem?.retail?.slice(1)?.replace(/,/g, "") || 0);
  return roundNumber(result);
};

export const getAdjustedExtendedRetail = (overShortItem: any) =>
  convertToCurrency("$", calculateOverShortSum(overShortItem));

export const calculateTransferCost = (overShortItem: any) => {
  const result =
    stringToNumber(overShortItem?.transferCost || "$0") *
    ((+overShortItem?.over || 0) - (+overShortItem?.short || 0));
  return roundNumber(result);
};

export const calculateTransferCosts = (
  transferCost: string,
  over: number,
  short: number
): number => {
  const result =
    stringToNumber(transferCost || "$0") * ((+over || 0) - (+short || 0));
  return roundNumber(result);
};

export const getTotalMargin = (
  state: any,
  sum: number,
  totTransferCost: number
) => {
  const absoluteSum = Math.abs(sum);
  const result =
    state.totAdjustedRetail && absoluteSum !== 0
      ? (((absoluteSum - totTransferCost) * 100) / absoluteSum).toFixed(2)
      : 0;
  return roundNumber(result);
};

export const checkIfShortEmpty = (state: any) =>
  !state?.overShortItemsCopy?.some((item: any) => Boolean(+item.short));

export const checkIfOverEmpty = (state: any) =>
  !state?.overShortItemsCopy?.some((item: any) => Boolean(+item.over));

export const overShortPoItemMapper = (item: any) => ({
  ...item,
  priceUnit: item.unit,
  netPrice: "$0",
  transferCost: formatCurrencyOrNumber(item.unitCost),
  landed: formatCurrencyOrNumber(item.unitCost),
  quantity: item.ordered,
  pack: item.packOrSize.split("/")[0],
  overEaches: "0",
  overCases: "0",
  shortEaches: "0",
  shortCases: "0",
  received: item.qtyShipped,
  receivedEaches: calculateEaches(item, "O/S"),
  uniqueId: uuidv4(),
  inputDisabled: {
    shortCases: item.isRandomWeight || item.palletModId,
    overCases: item.isRandomWeight || item.palletModId
  }
});

const getQtyEaches = (item: {
  received: string;
  packOrSize: string;
  isRandomWeight: boolean;
}) =>
  item.received
    ? getQuantityInEaches(item.received, item.packOrSize, item.isRandomWeight)
    : "0";

const getQtyCases = (item: {
  received: string;
  palletModId: string;
  isRandomWeight: boolean;
}) => (item.isRandomWeight || item.palletModId ? "0" : item.received || "0");

const getOverAndShortQtyValues = (
  columnName: string,
  isRandomWeightOrComponentItem: boolean,
  qtyCases: string | number,
  qtyEaches: string | number
) => {
  if (columnName === "over") {
    return {
      overEaches: isRandomWeightOrComponentItem ? qtyEaches : "0",
      overCases: isRandomWeightOrComponentItem ? "0" : qtyCases,
      shortEaches: "0",
      shortCases: "0"
    };
  }

  if (columnName === "short") {
    return {
      overEaches: "0",
      overCases: "0",
      shortEaches: isRandomWeightOrComponentItem ? qtyEaches : "0",
      shortCases: isRandomWeightOrComponentItem ? "0" : qtyCases
    };
  }
};

export const getOverShortReceivedValues = (
  item: any,
  headerId: string,
  selectedState: boolean
) => {
  if (item.isPalletMod) {
    return item;
  }

  const itemQtyInEaches = getQtyEaches(item);
  const itemQtyInCases = getQtyCases(item);

  const isRandomWeightOrComponentItem = item.isRandomWeight || item.palletModId;

  if (!selectedState) {
    item.overEaches = item.initalOverEaches || "0";
    item.overCases = item.initalOverCases || "0";
    item.shortEaches = item.initalShortEaches || "0";
    item.shortCases = item.initalShortCases || "0";

    item.inputDisabled = {
      overCases: isRandomWeightOrComponentItem,
      overEaches: false,
      shortCases:
        item.addedProduct || isRandomWeightOrComponentItem
          ? true
          : !!(+item?.initalOverCases || +item?.initalOverEaches),
      shortEaches: item.addedProduct
        ? true
        : !!(+item?.initalOverCases || +item?.initalOverEaches)
    };

    return item;
  }

  const { overCases, overEaches, shortEaches, shortCases } =
    getOverAndShortQtyValues(
      headerId,
      isRandomWeightOrComponentItem,
      itemQtyInCases,
      itemQtyInEaches
    );

  item.overCases = overCases;
  item.overEaches = overEaches;
  item.shortCases = shortCases;
  item.shortEaches = shortEaches;

  item.inputDisabled = {
    overCases: true,
    overEaches: true,
    shortCases: true,
    shortEaches: true
  };

  return item;
};

export const getUpdatedOsItemsWithCostAndMargin = (
  osItems: any,
  palletId: string,
  headerId: string,
  selectedState: boolean
) => {
  let sum = 0;
  let totalSumForDefaultItems = 0;
  let totTransferCost = 0;

  const updatedOsItems = osItems.map((overShortItem: any) => {
    if (overShortItem.palletId !== palletId) {
      totTransferCost = roundNumber(
        totTransferCost + calculateTransferCost(overShortItem)
      );
      sum = roundNumber(sum + calculateOverShortSum(overShortItem));
      totalSumForDefaultItems = overShortItem?.transferCost
        ? roundNumber(
            totalSumForDefaultItems + calculateOverShortSum(overShortItem)
          )
        : 0;
      return overShortItem;
    }

    overShortItem = getOverShortReceivedValues(
      overShortItem,
      headerId,
      selectedState
    );

    overShortItem.over =
      +overShortItem.overCases * +overShortItem.pack +
      +overShortItem.overEaches +
      "";

    overShortItem.short =
      +overShortItem.shortCases * +overShortItem.pack +
      +overShortItem.shortEaches +
      "";

    overShortItem.adjustedExtendedRetail =
      getAdjustedExtendedRetail(overShortItem);

    totTransferCost = roundNumber(
      totTransferCost + calculateTransferCost(overShortItem)
    );
    sum = roundNumber(sum + calculateOverShortSum(overShortItem));
    totalSumForDefaultItems = overShortItem?.transferCost
      ? roundNumber(
          totalSumForDefaultItems + calculateOverShortSum(overShortItem)
        )
      : 0;

    return overShortItem;
  });

  return { updatedOsItems, sum, totTransferCost, totalSumForDefaultItems };
};
